import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { AuthProvider } from 'react-oidc-context';
import App from './App';
import MaintenanceBanner from './components/shell/MaintenanceBanner';
import { AuthAvailableContext } from './hooks/useOptionalAuth';
import { ThemeProvider } from './hooks/useTheme';
import './index.css';

// OIDC Configuration for Keycloak
// The realm is determined from URL param or localStorage - no default
//
// I7 (hardening): this runs at MODULE SCOPE, before React ever mounts and
// before ThemeProvider/AuthProvider/any error boundary exists. A
// `localStorage` throw here (private-browsing / locked-down webview storage
// restrictions) is a dead script — a blank page with nothing to catch it,
// unlike a throw inside a component render. Mirrors
// hooks/useTheme.tsx's readStoredTheme/writeStoredTheme hardening: degrade to
// "no realm" (same as a first-time visitor, who then sees the realm-picker
// login screen) rather than letting the throw propagate.
const getRealmFromUrl = (): string | null => {
  const params = new URLSearchParams(window.location.search);
  const urlRealm = params.get('realm');
  if (urlRealm) {
    try {
      localStorage.setItem('billing_realm', urlRealm);
    } catch {
      // Storage unavailable — the realm still works for this page load via
      // the returned value below, it just won't persist across reloads.
    }
    return urlRealm;
  }
  try {
    return localStorage.getItem('billing_realm');
  } catch {
    return null;
  }
};

const realm = getRealmFromUrl();

// Only configure OIDC if realm is set
const oidcConfig = realm ? {
  // encodeURIComponent guards against a realm value containing `../` path
  // traversal segments. It can't change the host (an `@` lands in the path,
  // not the authority), but the realm ultimately comes from a URL query
  // param or localStorage, so it's untrusted input either way.
  authority: `https://iot.utilsync.cloud/auth/realms/${encodeURIComponent(realm)}`,
  client_id: 'billing-app',
  redirect_uri: window.location.origin + '/callback',
  post_logout_redirect_uri: window.location.origin,
  scope: 'openid profile email roles',
  automaticSilentRenew: true,
  accessTokenExpiringNotificationTimeInSeconds: 30,
  onSigninCallback: () => {
    // Remove code/state from URL after successful login
    window.history.replaceState({}, document.title, window.location.pathname);
  },
} : null;

// Wrapper component to handle missing realm
function AppWrapper() {
  if (!oidcConfig) {
    // No realm configured - App will show login page to select realm
    return (
      <ThemeProvider>
        <MaintenanceBanner />
        <AuthAvailableContext.Provider value={false}>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </AuthAvailableContext.Provider>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <MaintenanceBanner />
      <AuthAvailableContext.Provider value={true}>
        <AuthProvider {...oidcConfig}>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </AuthProvider>
      </AuthAvailableContext.Provider>
    </ThemeProvider>
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <AppWrapper />
  </React.StrictMode>
);
